import React, { useEffect, useState } from 'react';
import FormControl from "@mui/material/FormControl";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from '@mui/material/Grid';
import * as dates from '@simplybenefits/sb-dates';
import Modal from './Modal.js';
import Text from '../Text.js';
import BubbleSelectBlock from '../BubbleSelectBlock.js';
import TextInput from '../../components/TextFields/TextInput.js';
import InfoTip from '../InfoTip.js';
import EffectiveDateModal from '../Modals/EffectiveDateModal.js';
import i18n from '../../localization/i18n';
import tokens from '../../localization/tokens.js';
import { validateInput } from '../../services/index.js';
import { parseUserDataForAddUpdate } from './EmployeeDetailsModal/parseEmployeeData.js';
const initialState = {
  organization: '',
  insuranceCarrier: '',
  certificateNumber: '',
  acceptedTerms: false
};
const initialErrorState = {
  organizationError: false,
  insuranceCarrier: false,
  certificateNumber: false,
  acceptedTermsError: false
};

/**
 * 
 * @param {*} param0 
 * @returns 
 */
const WaiveCoverageModal = ({
  open,
  close,
  openChangeRequestModal,
  waiveCoverage,
  // need these props when using this modal from the master site
  userData,
  classData,
  divisions,
  isStudent,
  isMasterSite = false,
  loader
}) => {
  const [waiveFor, setWaiveFor] = useState(false);
  const [hasWaivedBenefits, setHasWaivedBenefits] = useState(false);
  const [comparableCoverage, setComparableCoverage] = useState(initialState);
  const [errorState, setErrorState] = useState(initialErrorState);
  const [effectiveDateError, setEffectiveDateError] = useState(false);
  const [showEffectiveDateModal, setShowEffectiveDateModal] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState(null);
  const {
    organizationError,
    insuranceCarrierError,
    certificateNumberError,
    acceptedTermsError
  } = errorState;
  const {
    organization,
    insuranceCarrier,
    certificateNumber,
    acceptedTerms
  } = comparableCoverage;
  useEffect(() => {
    if (!waiveFor) {
      setHasWaivedBenefits(false);
    }
  }, [waiveFor]);

  /**
   * 
   * @param {*} field 
   * @param {*} value 
   */
  const handleInputChange = (field, value) => {
    setComparableCoverage(prevState => ({
      ...prevState,
      [field]: value
    }));
  };

  /**
   * 
   */
  const resetForm = () => {
    setWaiveFor(false);
    setHasWaivedBenefits(false);
    setErrorState(initialErrorState);
    setComparableCoverage(initialState);
  };

  /**
   * 
   */
  const onClose = () => {
    resetForm();
    close();
  };

  /**
   * 
   * @returns 
   */
  const submit = async () => {
    const noFutureDates = false;
    const noPastDates = false;
    const mustBeFirstOfMonth = false;
    const error = dates.validate(effectiveDate, noFutureDates, noPastDates, mustBeFirstOfMonth, i18n.locale);
    if (error) {
      setEffectiveDateError(error);
      return false;
    }

    // this is called from the master site from the member profile, which requires sending some extra data
    const isProfile = true;
    const isEdit = true;
    const data = parseUserDataForAddUpdate(userData, classData, divisions, isStudent, isMasterSite, isEdit, isProfile);
    const comparableCoverageData = {
      waiveFor,
      hasWaived: hasWaivedBenefits,
      comparableCoverageEmployer: organization,
      comparableCoverageCarrier: insuranceCarrier,
      comparableCoverageCert: certificateNumber
    };
    const updateData = {
      ...data,
      ...comparableCoverageData,
      effectiveDate: dates.getUTC(effectiveDate)
    };
    await waiveCoverage(userData.userID, updateData, isProfile);
    onClose();
    return true;
  };

  /**
   * 
   * @returns 
   */
  const validateFormAndOpenChangeRequest = async () => {
    let errors = {};
    errors.organizationError = await validateInput('checkEmpty', organization);
    errors.insuranceCarrierError = await validateInput('checkEmpty', insuranceCarrier);
    errors.certificateNumberError = await validateInput('checkEmpty', certificateNumber);
    errors.acceptedTermsError = !acceptedTerms;
    const hasErrors = Object.values(errors).some(Boolean); // Check for any errors

    if (hasErrors) {
      setErrorState({
        ...errors
      });
      return false;
    }
    if (isMasterSite) {
      return setShowEffectiveDateModal(true);
    }
    const changeRequestData = {
      type: 'Waive Benefits',
      details: {
        waiveFor,
        hasWaived: hasWaivedBenefits,
        comparableCoverageEmployer: organization,
        comparableCoverageCarrier: insuranceCarrier,
        comparableCoverageCert: certificateNumber
      }
    };
    openChangeRequestModal(changeRequestData);
    onClose();
    return true;
  };
  if (showEffectiveDateModal) {
    return /*#__PURE__*/React.createElement(EffectiveDateModal, {
      open: showEffectiveDateModal,
      onClose: () => setShowEffectiveDateModal(false),
      effectiveDate: effectiveDate,
      setEffectiveDate: setEffectiveDate,
      effectiveDateError: effectiveDateError,
      loader: loader,
      employeeMeta: userData,
      isMasterProfile: isMasterSite,
      onClick: submit
    });
  }
  return /*#__PURE__*/React.createElement(Modal, {
    open: open,
    medium: true,
    onClose: onClose,
    title: i18n.t(tokens.labels.changeRequests.waiveCoverage),
    primaryButton: {
      label: i18n.t(tokens.labels.changeRequests.waiveCoverage),
      onClick: validateFormAndOpenChangeRequest
    }
  }, /*#__PURE__*/React.createElement(Text, {
    small: true,
    light: true
  }, i18n.t(tokens.labels.changeRequests.waiveCoverageForBenefits)), /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true,
    margin: "20px 0 10px 0"
  }, i18n.t(tokens.labels.changeRequests.waiveCoverageFor)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    allowToggleOff: true,
    first: {
      title: i18n.t(tokens.labels.signup.myselfAndDependents),
      selected: waiveFor === 'Self-Dependents',
      field: 'waiveSelfAndDependents',
      customStyle: {
        fontSize: 12
      }
    },
    second: {
      title: i18n.t(tokens.labels.signup.dependentsOnly),
      selected: waiveFor === 'Dependents',
      field: 'waiveDependents',
      customStyle: {
        fontSize: 12
      }
    },
    handleChange: field => {
      if (field === 'waiveSelfAndDependents') {
        setWaiveFor("Self-Dependents");
        waiveFor !== "Self-Dependents" ? setWaiveFor("Self-Dependents") : setWaiveFor(false);
      }
      if (field === 'waiveDependents') {
        setWaiveFor("Dependents");
        waiveFor !== "Dependents" ? setWaiveFor("Dependents") : setWaiveFor(false);
      }
    }
  }), /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true,
    margin: "20px 0 10px 0"
  }, i18n.t(tokens.labels.signup.forTheFollowingBenefits)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    first: {
      title: i18n.t(tokens.labels.changeRequests.extendedHealth),
      selected: hasWaivedBenefits === 'Health',
      field: 'hasWaivedHealth',
      customStyle: {
        fontSize: 12
      },
      rightIcon: /*#__PURE__*/React.createElement(InfoTip, {
        active: hasWaivedBenefits === 'Health'
      }),
      onClickRightIcon: () => null // need this prop for the icon to be visible
    },
    second: {
      title: i18n.t(tokens.titles.dental),
      selected: hasWaivedBenefits === 'Dental',
      field: 'hasWaivedDental',
      customStyle: {
        fontSize: 12
      }
    },
    third: {
      title: i18n.t(tokens.labels.changeRequests.extendedHealthCareDental),
      selected: hasWaivedBenefits === 'Health-Dental',
      field: 'hasWaivedBoth',
      customStyle: {
        fontSize: 12
      }
    },
    handleChange: field => {
      if (field === 'hasWaivedHealth') {
        setHasWaivedBenefits("Health");
      } else if (field === 'hasWaivedDental') {
        setHasWaivedBenefits('Dental');
      } else if (field === 'hasWaivedBoth') {
        setHasWaivedBenefits('Health-Dental');
      }
    }
  }), /*#__PURE__*/React.createElement(Text, {
    bold: true,
    small: true,
    margin: "20px 0 10px 0"
  }, i18n.t(tokens.labels.changeRequests.comparableCoverage)), /*#__PURE__*/React.createElement(Text, {
    small: true
  }, i18n.t(tokens.labels.changeRequests.comparableCoverageDescription)), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    alignItems: "center",
    spacing: 2,
    sx: {
      marginBottom: 2.5
    }
  }, /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.changeRequests.organization),
    value: organization,
    onChange: e => handleInputChange('organization', e.target.value),
    required: true,
    fullWidth: true,
    error: Boolean(organizationError),
    helperText: organizationError
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.changeRequests.insuranceCarrier),
    value: insuranceCarrier,
    onChange: e => handleInputChange('insuranceCarrier', e.target.value),
    required: true,
    fullWidth: true,
    error: Boolean(insuranceCarrierError),
    helperText: insuranceCarrierError
  })), /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.signup.certificateNumber),
    value: certificateNumber,
    onChange: e => handleInputChange('certificateNumber', e.target.value),
    required: true,
    fullWidth: true,
    error: Boolean(certificateNumberError),
    helperText: certificateNumberError
  }))), /*#__PURE__*/React.createElement(FormControl, {
    fullWidth: true
  }, /*#__PURE__*/React.createElement(FormGroup, {
    row: true
  }, /*#__PURE__*/React.createElement(FormControlLabel, {
    control: /*#__PURE__*/React.createElement(Checkbox, {
      checked: Boolean(acceptedTerms),
      onChange: () => handleInputChange('acceptedTerms', !acceptedTerms)
    }),
    label: /*#__PURE__*/React.createElement(Text, {
      avMedium: true
    }, i18n.t(tokens.labels.changeRequests.confirmWaiveCoverage))
  })), acceptedTermsError ? /*#__PURE__*/React.createElement(FormHelperText, {
    error: true
  }, i18n.t(tokens.labels.banking.youMustAcceptTermsError)) : null));
};
export default WaiveCoverageModal;